// Lógica de la calculadora LucidFlex 25K, copiada tal cual de calculadora-lucidflex.html.
// Las fórmulas no se tocan: solo se han tipado y sacado a funciones puras.

export interface LucidCfg {
    start: number;
    target: number;
    mll: number;
    cons: number;
}

export interface LucidState {
    cfg: LucidCfg;
    days: number[];
}

export interface PassToday {
    lo: number;
    hi: number;
}

export interface LucidResult {
    T: number;
    best: number;
    req: number;
    remaining: number;
    passed: boolean;
    cap: number;
    passToday: PassToday | null;
    daysNeeded: number;
    bal: number;
    mllv: number;
    breach: boolean;
    c: number;
    target: number;
    days: number[];
    locked: boolean;
}

export type StatusCls = 'ok' | 'warn' | 'bad';

export const KEY = 'lucidflex25k-v1';
export const DEF: LucidState = { cfg: { start: 25000, target: 1250, mll: 1000, cons: 40 }, days: [] };
export const EPS = 0.005;

export function loadState(): LucidState {
    let st: any;
    try { st = JSON.parse(localStorage.getItem(KEY) as string) } catch (e) { }
    if (!st || !Array.isArray(st.days) || !st.cfg) st = JSON.parse(JSON.stringify(DEF));
    return st;
}

export function saveState(st: LucidState) {
    try { localStorage.setItem(KEY, JSON.stringify(st)) } catch (e) { }
}

export const fmt = (v: number) => { const s = v < 0 ? '−' : ''; return s + '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) };

export function calc(st: LucidState): LucidResult {
    const { start, target, mll, cons } = st.cfg; const c = cons / 100;
    const days = st.days.map(Number).filter(v => isFinite(v));
    const T = days.reduce((a, b) => a + b, 0);
    const best = Math.max(0, ...days);
    const req = Math.max(target, best / c);
    const remaining = Math.max(0, req - T);
    const passed = days.length > 0 && T >= target - EPS && best <= c * T + EPS;
    const cap = Math.max(best, c * target);
    // ¿se puede pasar hoy?
    let passToday: PassToday | null = null;
    if (!passed) {
        const need = Math.max(target - T, best / c - T);
        const X = Math.max(need, 0.01);
        const nb = Math.max(best, X);
        if (nb <= c * (T + X) + EPS) {
            const hi = Math.max(best, T > 0 ? c * T / (1 - c) : 0);
            passToday = { lo: X, hi: Math.max(hi, X) };
        }
    }
    const daysNeeded = remaining > 0 ? Math.ceil((remaining - EPS) / cap) : 0;
    // drawdown
    let bal = start, hi = start, breach = false, mllv = start - mll;
    const lock = start + 100;
    days.forEach(d => { bal += d; if (bal > hi) hi = bal; mllv = Math.min(Math.max(start - mll, hi - mll), lock); if (bal <= mllv + EPS && d < 0) breach = true; });
    return { T, best, req, remaining, passed, cap, passToday, daysNeeded, bal, mllv, breach, c, target, days, locked: mllv >= lock };
}

export function statusOf(r: LucidResult): { cls: StatusCls; txt: string } {
    let cls: StatusCls, txt: string;
    if (r.breach) { cls = 'bad'; txt = 'Cuenta quemada' }
    else if (r.passed) { cls = 'ok'; txt = 'Fase pasada' }
    else if (r.best > r.c * r.target + EPS) { cls = 'warn'; txt = 'Objetivo ampliado' }
    else { cls = 'ok'; txt = 'En regla' }
    return { cls, txt };
}

export function todayMessage(r: LucidResult): string {
    let m: string;
    if (r.breach) m = 'El balance ha tocado el límite de drawdown.';
    else if (r.passed) m = 'Cumples objetivo y consistencia. No hace falta operar más.';
    else if (r.passToday) {
        const lo = r.passToday.lo, hi = r.passToday.hi;
        m = Math.abs(hi - lo) < 1 ? `Pasas hoy si cierras con ${fmt(lo)}.` : `Pasas hoy si cierras entre ${fmt(lo)} y ${fmt(hi)}.`;
    } else {
        const per = r.remaining / r.daysNeeded;
        m = `Te faltan ${fmt(r.remaining)}. Mínimo ${r.daysNeeded} día${r.daysNeeded > 1 ? 's' : ''} más, unos ${fmt(Math.ceil(per))} por día.`;
        if (r.T < 0) m += ' Las pérdidas no bajan el tope diario, solo añaden días.';
        else if (r.best > r.c * r.target + EPS) m += ` Tu mejor día (${fmt(r.best)}) sube el objetivo a ${fmt(r.req)}.`;
    }
    return m;
}

export function ddMessage(r: LucidResult): string {
    return r.locked ? 'El límite está bloqueado y ya no sube más.' : `Si mañana tocas ${fmt(r.mllv)} en cualquier momento, la cuenta se quema. El límite sube con tu mejor cierre y nunca baja.`;
}

// Etiqueta de cada fila de día (mismo criterio que el original)
export function dayTag(v: number, r: LucidResult): { cls: StatusCls | null; txt: string } {
    if (v < 0) return { cls: 'bad', txt: 'Pérdida' };
    if (v > 0 && v === r.best) {
        if (v > r.c * r.target + EPS) return { cls: 'warn', txt: 'Mejor · >40%' };
        return { cls: 'ok', txt: 'Mejor día' };
    }
    return { cls: null, txt: '' };
}
